import { useState } from 'react';
import { Plane, Code, Palette, Briefcase, GraduationCap } from 'lucide-react';

/**
 * Kurs kapak gorseli — tek kaynak.
 *
 * `thumbnail` varsa ve yuklenirse gercek gorsel; bos, gecersiz ya da yuklenemezse
 * (onError) kategori gradient'i + kategori ikonu yer tutucusu. Katalog, detay,
 * favoriler ve checkout ayni bilesenden gectigi icin yer tutucu her yerde ayni.
 */

const DEFAULT_STYLE = { from: '#1D6FA4', to: '#0E4C8C', iconColor: '#FFFFFF', Icon: GraduationCap };

export const COURSE_CATEGORY_STYLES = {
  cabin_crew:  { from: '#0E7A55', to: '#1D6FA4', iconColor: '#FFFFFF', Icon: Plane },
  programming: { from: '#1D6FA4', to: '#0E4C8C', iconColor: '#FFFFFF', Icon: Code },
  design:      { from: '#CE82FF', to: '#9B59B6', iconColor: '#FFFFFF', Icon: Palette },
  business:    { from: '#B8860B', to: '#8B6914', iconColor: '#FFFFFF', Icon: Briefcase },
};

export function getCourseCategoryStyle(category) {
  return COURSE_CATEGORY_STYLES[category] || DEFAULT_STYLE;
}

/**
 * @param {string} [thumbnail]   course.thumbnail
 * @param {string} [category]    course.category (bilinmeyense varsayilan gradient+ikon)
 * @param {string} [alt]         gercek gorsel icin alt; kartta baslik zaten varsa bos birak
 * @param {string} [className]   kapsayici boyut/yuvarlaklik (h-36, aspect-video, w-24 h-16 ...)
 * @param {string} [imgClassName] gorsele ek sinif (hover olcekleme vb.)
 * @param {number} [iconSize]    yer tutucu ikon boyutu (px)
 * @param {object} [style]       kapsayiciya ek stil (borderBottom vb.)
 * @param {React.ReactNode} [children] rozet gibi ust katman ogeleri (absolute)
 */
export default function CourseCover({
  thumbnail,
  category,
  alt = '',
  className = '',
  imgClassName = '',
  iconSize = 40,
  style,
  children,
  ...rest
}) {
  const src = typeof thumbnail === 'string' ? thumbnail.trim() : '';
  // Hata alan src'yi hatirla: thumbnail degisirse yeni src tekrar denenir, ayni src dongu kurmaz.
  const [failedSrc, setFailedSrc] = useState(null);
  const showImage = Boolean(src) && failedSrc !== src;
  const { from, to, iconColor, Icon } = getCourseCategoryStyle(category);

  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden ${className}`}
      style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`, ...style }}
      {...rest}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailedSrc(src)}
          className={`absolute inset-0 w-full h-full object-cover ${imgClassName}`}
        />
      ) : (
        <div
          data-testid="course-cover-placeholder"
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 4px 4px, rgba(255,255,255,0.12) 2px, transparent 2.5px)',
              backgroundSize: '16px 16px',
            }}
          />
          <Icon size={iconSize} strokeWidth={2} className="relative z-10 drop-shadow-sm" style={{ color: iconColor }} />
        </div>
      )}
      {children}
    </div>
  );
}
